import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import CountrySelect from '@/components/common/CountrySelect';
import { Badge } from '@/components/ui/badge';
import {
  Globe, Loader2, Save, Plus, Pencil, Trash2, Eye, EyeOff,
  Send, FileText, X, User, ExternalLink, Upload, Mail, Briefcase,
} from 'lucide-react';
import MarkdownEditor from '@/components/advisor/common/MarkdownEditor';
import PhotoCropModal from '@/components/advisor/common/PhotoCropModal';

interface AdvisorProfile {
  id: string;
  organization_id: string;
  display_name: string;
  headline: string;
  bio: string;
  photo_url: string;
  photo_data: string;
  industries: string;
  certifications: string[];
  years_experience: number | null;
  specializations: string;
  languages: string;
  country: string;
  cities: string;
  contact_email: string;
  booking_url: string;
  website_url: string;
  linkedin_url: string;
  stats?: { clients: number; running_contracts: number; published_articles: number };
  is_published: boolean;
  blog_post_count: number;
  created_at: string;
  updated_at: string;
}


const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-slate-500/10 text-slate-700 border-slate-500/30',
  published: 'bg-green-500/10 text-green-700 border-green-500/30',
};

function TagChipInput({ value, onChange, placeholder, ...props }: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  [key: string]: any;
}) {
  const tags = value ? value.split(',').map(t => t.trim()).filter(Boolean) : [];
  const [inputValue, setInputValue] = useState('');

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed].join(', '));
    }
    setInputValue('');
  };

  const removeTag = (tag: string) => {
    onChange(tags.filter(t => t !== tag).join(', '));
  };

  return (
    <div className="flex flex-wrap gap-1 p-1.5 min-h-[36px] rounded-md border bg-background" {...props}>
      {tags.map(tag => (
        <span key={tag} className="inline-flex items-center gap-0.5 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-xs px-2 py-0.5 rounded-full border border-amber-500/20">
          {tag}
          <button
            type="button"
            className="hover:text-red-500 ml-0.5"
            onClick={() => removeTag(tag)}
          >
            ×
          </button>
        </span>
      ))}
      <input
        className="flex-1 min-w-[80px] bg-transparent text-xs outline-none px-1"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag(inputValue);
          }
          if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
            removeTag(tags[tags.length - 1]);
          }
        }}
        onBlur={() => { if (inputValue.trim()) addTag(inputValue); }}
        placeholder={tags.length === 0 ? placeholder : ''}
      />
    </div>
  );
}

interface TemplateTeaser {
  id: string;
  title: string;
  industry: string;
  teaser: string;
  show_on_public_profile: boolean;
}

export default function ProfilePage() {
  const queryClient = useQueryClient();
  const [editingProfile, setEditingProfile] = useState(false);

  // Profile form state
  const [profileFields, setProfileFields] = useState<Partial<AdvisorProfile>>({});



  // Fetch profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['advisor-profile-mine'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      try {
        const res = await api.get('/advisor/profile/mine/');
        return res.data as AdvisorProfile;
      } catch (e: any) {
        if (e.response?.status === 404) return null;
        throw e;
      }
    },
  });


  // Profile mutations
  const createProfileMutation = useMutation({
    mutationFn: async (data: Partial<AdvisorProfile>) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/advisor/profile/', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advisor-profile-mine'] });
      setEditingProfile(false);
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<AdvisorProfile> }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.patch(`/advisor/profile/${id}/`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advisor-profile-mine'] });
      setEditingProfile(false);
    },
  });

  const [cropFile, setCropFile] = useState<File | null>(null);

  const uploadPhotoMutation = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const api = await authenticatedBitswanBackendInstance();
      const formData = new FormData();
      formData.append('photo', file);
      return api.post(`/advisor/profile/${id}/upload-photo/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['advisor-profile-mine'] });
      // Update local edit state with the new photo URL
      const data = res.data;
      if (data?.photo_url || data?.photo_data) {
        setProfileFields((prev) => ({
          ...prev,
          photo_url: data.photo_url || prev.photo_url,
          photo_data: data.photo_data || prev.photo_data,
        }));
      }
    },
  });

  // Blog mutations





  const startEditProfile = () => {
    if (profile) {
      setProfileFields({ ...profile });
    } else {
      setProfileFields({ display_name: '', headline: '', bio: '' });
    }
    setEditingProfile(true);
  };

  const saveProfile = () => {
    if (profile) {
      updateProfileMutation.mutate({ id: profile.id, data: profileFields });
    } else {
      createProfileMutation.mutate(profileFields);
    }
  };

  const startEditPost = (post: BlogPost) => {
    setEditingPostId(post.id);
    setEditTitle(post.title);
    setEditExcerpt(post.excerpt);
    setEditContent(post.content);
    setEditTags(post.tags);
  };

  const updateField = (key: string, value: string | number | boolean) => {
    setProfileFields({ ...profileFields, [key]: value });
  };

  if (profileLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Public Profile & Blog</h2>
          <p className="text-muted-foreground">Manage your public-facing advisor profile and publish articles</p>
        </div>
        {!editingProfile && (
          <Button onClick={startEditProfile} data-testid="edit-profile-btn">
            <Pencil className="h-4 w-4 mr-2" />
            {profile ? 'Edit Profile' : 'Create Profile'}
          </Button>
        )}
      </div>

      {/* Profile Section */}
      {editingProfile ? (
        <Card className="border-amber-500/50">
          <CardHeader>
            <CardTitle className="text-base">{profile ? 'Edit Profile' : 'Create Profile'}</CardTitle>
            <CardDescription>Your public-facing advisor information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Display Name *</label>
                <Input
                  value={profileFields.display_name || ''}
                  onChange={(e) => updateField('display_name', e.target.value)}
                  placeholder="Your name or firm name"
                  data-testid="profile-display-name"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Headline</label>
                <Input
                  value={profileFields.headline || ''}
                  onChange={(e) => updateField('headline', e.target.value)}
                  placeholder="Automation Advisor | Legal & Logistics"
                  data-testid="profile-headline"
                />
              </div>
            </div>

            <div data-testid="bio-editor-container">
              <label className="text-xs text-muted-foreground">Bio</label>
              <div className="mt-1">
                <MarkdownEditor
                  value={profileFields.bio || ''}
                  onChange={(val) => updateField('bio', val)}
                  height="200px"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Industries</label>
                <TagChipInput
                  value={profileFields.industries || ''}
                  onChange={(val) => updateField('industries', val)}
                  placeholder="Add industry..."
                  data-testid="industries-input"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Specializations</label>
                <TagChipInput
                  value={profileFields.specializations || ''}
                  onChange={(val) => updateField('specializations', val)}
                  placeholder="Add specialization..."
                  data-testid="specializations-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Languages</label>
                <Input
                  value={profileFields.languages || ''}
                  onChange={(e) => updateField('languages', e.target.value)}
                  placeholder="English, Czech, German"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Country</label>
                <CountrySelect value={profileFields.country} onChange={(name) => updateField('country', name)} testId="profile-country" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Cities</label>
                <Input
                  value={profileFields.cities || ''}
                  onChange={(e) => updateField('cities', e.target.value)}
                  placeholder="Prague, Brno"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Years Experience</label>
                <Input
                  type="number" min="0"
                  value={profileFields.years_experience ?? ''}
                  onChange={(e) => updateField('years_experience', parseInt(e.target.value) || 0)}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Profile Photo</label>
                <div className="mt-1 flex items-center gap-3">
                  {(profileFields.photo_data || profileFields.photo_url) ? (
                    <img
                      src={profileFields.photo_data || profileFields.photo_url}
                      alt="Profile"
                      className="h-12 w-12 rounded-full object-cover"
                      data-testid="photo-preview"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center" data-testid="photo-placeholder">
                      <User className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <label className="cursor-pointer" data-testid="photo-upload-label">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      data-testid="photo-upload-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setCropFile(file);
                        }
                        e.target.value = '';
                      }}
                    />
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border rounded-md hover:bg-muted">
                      <Upload className="h-3 w-3" />
                      {uploadPhotoMutation.isPending ? 'Uploading...' : 'Upload Photo'}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {cropFile && profile && (
              <PhotoCropModal
                file={cropFile}
                onCrop={(croppedFile) => {
                  uploadPhotoMutation.mutate({ id: profile.id, file: croppedFile });
                  setCropFile(null);
                }}
                onCancel={() => setCropFile(null)}
              />
            )}

            <div data-testid="certifications-editor">
              <label className="text-xs text-muted-foreground">Professional certifications</label>
              <div className="mt-1 space-y-1.5">
                {[...(profileFields.certifications ?? []), ''].map((item, index, all) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={item}
                      placeholder={index === all.length - 1 ? 'Add a certification…' : ''}
                      onChange={(e) => {
                        const next = [...(profileFields.certifications ?? [])];
                        next[index] = e.target.value;
                        setProfileFields({ ...profileFields, certifications: next.filter((c, i) => c.trim() || i < next.length - 1) });
                      }}
                      data-testid={`certification-${index}`}
                    />
                    {index < all.length - 1 && (
                      <Button variant="ghost" size="sm" onClick={() => setProfileFields({
                        ...profileFields,
                        certifications: (profileFields.certifications ?? []).filter((_, i) => i !== index),
                      })} aria-label="Remove certification">
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Contact Email</label>
                <Input
                  type="email"
                  value={profileFields.contact_email || ''}
                  onChange={(e) => updateField('contact_email', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Booking URL</label>
                <Input
                  value={profileFields.booking_url || ''}
                  onChange={(e) => updateField('booking_url', e.target.value)}
                  placeholder="https://calendly.com/..."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Website</label>
                <Input
                  value={profileFields.website_url || ''}
                  onChange={(e) => updateField('website_url', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">LinkedIn</label>
                <Input
                  value={profileFields.linkedin_url || ''}
                  onChange={(e) => updateField('linkedin_url', e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={saveProfile}
                disabled={!profileFields.display_name || createProfileMutation.isPending || updateProfileMutation.isPending}
                data-testid="save-profile-btn"
              >
                {(createProfileMutation.isPending || updateProfileMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="h-4 w-4 mr-2" />
                {profile ? 'Save Profile' : 'Create Profile'}
              </Button>
              <Button variant="outline" onClick={() => setEditingProfile(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      ) : profile ? (
        <Card>
          <CardContent className="flex items-start gap-4 py-4">
            <div className="h-14 w-14 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
              {(profile.photo_data || profile.photo_url) ? (
                <img src={profile.photo_data || profile.photo_url} alt="" className="h-14 w-14 rounded-full object-cover" data-testid="profile-photo" />
              ) : (
                <User className="h-7 w-7 text-amber-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="font-semibold">{profile.display_name}</h3>
                {profile.is_published ? (
                  <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium bg-green-500/10 text-green-700 border-green-500/30">
                    Published
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium bg-slate-500/10 text-slate-600 border-slate-500/30">
                    Draft
                  </span>
                )}
              </div>
              {profile.headline && <p className="text-sm text-muted-foreground">{profile.headline}</p>}
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                {profile.industries && <span>{profile.industries}</span>}
                {profile.languages && <span>{profile.languages}</span>}
                {profile.country && <span>{profile.country}{profile.cities ? ` (${profile.cities})` : ''}</span>}
                {profile.years_experience && <span>{profile.years_experience} years experience</span>}
                <span>{profile.blog_post_count} published articles</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {profile.is_published && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`/public/advisor/${profile.id}`, '_blank')}
                  data-testid="view-public-profile"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View Public Profile
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const api_call = async () => {
                    const api = await authenticatedBitswanBackendInstance();
                    await api.patch(`/advisor/profile/${profile.id}/`, {
                      is_published: !profile.is_published,
                    });
                    queryClient.invalidateQueries({ queryKey: ['advisor-profile-mine'] });
                  };
                  api_call();
                }}
              >
                {profile.is_published ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                {profile.is_published ? 'Unpublish' : 'Publish'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Globe className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground mb-4">No public profile yet. Create one to attract prospective clients.</p>
            <Button onClick={startEditProfile} data-testid="edit-profile-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Profile
            </Button>
          </CardContent>
        </Card>
      )}

      {profile && profile.stats && (
        <Card data-testid="profile-stats">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Eye className="h-4 w-4 text-amber-500" /> Your practice in numbers
            </CardTitle>
            <CardDescription>Counted from your clients, contracts and articles.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold" data-testid="stat-clients">{profile.stats.clients}</div>
                <div className="text-xs text-muted-foreground">Clients on BitSwan</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-600" data-testid="stat-contracts">{profile.stats.running_contracts}</div>
                <div className="text-xs text-muted-foreground">Running contracts</div>
              </div>
              <div>
                <div className="text-2xl font-bold" data-testid="stat-articles">{profile.stats.published_articles}</div>
                <div className="text-xs text-muted-foreground">Published articles</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
